import { Injectable, inject } from "@angular/core";
import { forkJoin, map, Observable } from "rxjs";
import { State } from "../types/state";
import { IndividualApiService, QrtApiService } from "./api-services";
import { DEFAULT_SESSION_PERMISSIONS, SessionPermissions } from "../types/permissions";
import { resolveSessionPermissions } from "./permission-resolver";
import { AuthService } from "./auth.service";
import { User } from "../types/user";

@Injectable({ providedIn: 'root' })
export class UserService {

    private qrtApiService = inject(QrtApiService);
    private individualApiService = inject(IndividualApiService);
    private authService = inject(AuthService);

    getUser(): Observable<User | null> {
      return forkJoin([
        this.getUserInfo(),
        this.getUserPermissions(),
        this.getUserDefaultState(),
      ]).pipe(
        map(([userInfo, permissions, defaultState]) => {
          if (!userInfo) {
            return null;
          }

          const user: User = {
            guid: userInfo.guid,
            name: userInfo.name,
            permissions: permissions ?? DEFAULT_SESSION_PERMISSIONS,
            defaultState: defaultState ?? null,
          };

          return user;
        })
      );
    }

    getUserInfo(): Observable<{ guid: string, name: string } | null> {
      return this.authService.getJsonTokenDataFromLoggedUser().pipe(
        map(response => ({
            guid: response.guid ?? '',
            name: `${response.firstName?.trim() ?? ''} ${response.lastName?.trim() ?? ''}`,
        })),
      );
    }

    getUserPermissions(): Observable<SessionPermissions | null> {
        return this.individualApiService.getLoggedInGroups().pipe(
            map(response => {
                return resolveSessionPermissions(response);
            })
        )
    }

    getUserDefaultState(): Observable<State | null> {
        const USER_ASSOCIATED_STATE_REPORT_GUID = '93057A24-5F7E-4F4C-A923-05E2DD4DE32B';

        return this.qrtApiService
        .generateReport(USER_ASSOCIATED_STATE_REPORT_GUID, []).pipe(
            map((rows) => {
                if (Array.isArray(rows) && rows.length > 0) {
                    const row = rows[0] as Record<string, string>;
                    if (row['State_Abbr'] && row['State']) {
                    const preferredState: State = {
                        abbreviation: row['State_Abbr'],
                        name: row['State'],
                    }

                    return preferredState;
                    }
                }

                return null;
            }),
        );
    }
}
